import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import moment from 'moment'
import { CaretDownIcon, XIcon } from '@phosphor-icons/react'
import { Tribe } from '../../types/Tribe'
import { Team } from '../../types/Team'
import { Role, TeamRoles, LeadershipRoleTypes } from '../../types/Roles'
import { User } from '../../types/User'
import { FormCheckbox } from './FormCheckbox'
import { NotificationSettingsModal } from './NotificationSettingsModal'

export type TeamAllocations = {
  [key: number]: { allocation: number; role: string }
}

export type MemberSettingsData = {
  isAdmin: boolean
  isGuildMaster: boolean
  isLeadership: boolean
  isStaff: boolean
  isGrowthWheelReviewer: boolean
  isGrowthWheelEditor: boolean
  selectedTribes: Tribe[]
  selectedTeams: Team[]
  teamAllocations: TeamAllocations
  selectedGuildIdsForGrowthWheelReviewer: number[]
  selectedTribeIdsForEmailNotification: number[]
  selectedGuildIdsForEmailNotification: number[]
}

export type MemberSettingsSectionRef = {
  getData: () => MemberSettingsData
  setAllocationError: (value: boolean) => void
  clearTeamsAndAllocations: () => void
}

interface MemberSettingsSectionProps {
  guilds: Role[]
  tribes: Tribe[]
  teams: Team[]
  selectedGuild: number
  user?: User
  onChange?: (data: MemberSettingsData) => void
}

export const MemberSettingsSection = forwardRef<
  MemberSettingsSectionRef,
  MemberSettingsSectionProps
>(function MemberSettingsSection(
  { guilds, tribes, teams, selectedGuild, user, onChange },
  ref,
) {
  const [isAdmin, setIsAdmin] = useState(false)
  const [isGuildMaster, setIsGuildMaster] = useState(false)
  const [isLeadership, setIsLeadership] = useState(false)
  const [isStaff, setIsStaff] = useState(false)
  const [isGrowthWheelReviewer, setIsGrowthWheelReviewer] = useState(false)
  const [isGrowthWheelEditor, setIsGrowthWheelEditor] = useState(false)
  const [selectedTribes, setSelectedTribes] = useState<Tribe[]>([])
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([])
  const [teamAllocations, setTeamAllocations] = useState<TeamAllocations>({})
  const [
    selectedGuildIdsForGrowthWheelReviewer,
    setSelectedGuildIdsForGrowthWheelReviewer,
  ] = useState<number[]>([])
  const [
    selectedTribeIdsForEmailNotification,
    setSelectedTribeIdsForEmailNotification,
  ] = useState<number[]>([])
  const [
    selectedGuildIdsForEmailNotification,
    setSelectedGuildIdsForEmailNotification,
  ] = useState<number[]>([])
  const [allocationError, setAllocationError] = useState(false)
  const [showNotifModal, setShowNotifModal] = useState(false)
  const [tempSelectedTribeIds, setTempSelectedTribeIds] = useState<number[]>([])
  const [tempSelectedGuildIds, setTempSelectedGuildIds] = useState<number[]>([])

  const getData = (): MemberSettingsData => ({
    isAdmin,
    isGuildMaster,
    isLeadership,
    isStaff,
    isGrowthWheelReviewer,
    isGrowthWheelEditor,
    selectedTribes,
    selectedTeams,
    teamAllocations,
    selectedGuildIdsForGrowthWheelReviewer,
    selectedTribeIdsForEmailNotification,
    selectedGuildIdsForEmailNotification,
  })

  useImperativeHandle(ref, () => ({
    getData,
    setAllocationError,
    clearTeamsAndAllocations: () => {
      setSelectedTeams([])
      setTeamAllocations({})
    },
  }))

  useEffect(() => {
    onChange?.(getData())
  }, [
    isAdmin,
    isGuildMaster,
    isLeadership,
    isStaff,
    isGrowthWheelReviewer,
    isGrowthWheelEditor,
    selectedTribes,
    selectedTeams,
    teamAllocations,
    selectedGuildIdsForGrowthWheelReviewer,
    selectedTribeIdsForEmailNotification,
    selectedGuildIdsForEmailNotification,
  ])

  useEffect(() => {
    if (!user) return

    setIsAdmin(user.isAdmin)
    setIsGuildMaster(user.isGuildMaster)
    setIsLeadership(user.isLeadership)
    setIsStaff(user.staffAugmentation)
    setIsGrowthWheelReviewer(user.isGrowthWheelReviewer)
    setIsGrowthWheelEditor(user.isGrowthWheelEditor)
    setSelectedGuildIdsForGrowthWheelReviewer(
      user.guildIdsForGrowthWheelReviewer,
    )
    setSelectedGuildIdsForEmailNotification(user.subscribedGuildIdsForEmail)
    setSelectedTribeIdsForEmailNotification(user.subscribedTribeIdsForEmail)

    if (user.tribes.length > 0) {
      const userSelectedTribes: Tribe[] = []
      user.tribes.forEach(userTribe => {
        const tribe = tribes.find(t => t.id === userTribe.id)
        if (tribe && !userSelectedTribes.includes(tribe))
          userSelectedTribes.push(tribe)
      })
      setSelectedTribes([...userSelectedTribes])
    }

    if (user.teamAllocations) {
      const allocations: TeamAllocations = {}
      const userSelectedTeams: Team[] = []
      user.teamAllocations.forEach(userTeam => {
        allocations[userTeam.teamId] = {
          allocation: userTeam.allocation,
          role: userTeam.role || '',
        }
        userSelectedTeams.push(userTeam.team)
      })
      setSelectedTeams([...userSelectedTeams])
      setTeamAllocations({ ...allocations })
    }
  }, [user, tribes])

  const selectableTeams = teams
    .filter(team => selectedTribes.find(tribe => tribe.id === team.tribeId))
    .filter(
      team => !selectedTeams.find(selectedTeam => selectedTeam.id === team.id),
    )

  const availableTeams = selectableTeams.filter(team => {
    if (!team.endDate || moment().isSameOrBefore(moment(team.startDate)))
      return true
    return moment().isBetween(
      moment(team.startDate),
      moment(team.endDate),
      undefined,
      '[]',
    )
  })

  const shouldRenderLabelsSelect = () => {
    const isLeadershipRole = LeadershipRoleTypes.find(
      leadershipRoleType =>
        leadershipRoleType ===
        guilds.find(guild => guild.id === selectedGuild)?.type,
    )
    return !isLeadershipRole && Object.keys(teamAllocations).length > 0
  }

  const openNotifModal = () => {
    setTempSelectedTribeIds(selectedTribeIdsForEmailNotification)
    setTempSelectedGuildIds(selectedGuildIdsForEmailNotification)
    setShowNotifModal(true)
  }

  const closeNotifModal = () => {
    setTempSelectedGuildIds(selectedGuildIdsForEmailNotification)
    setTempSelectedTribeIds(selectedTribeIdsForEmailNotification)
    setShowNotifModal(false)
  }

  const saveNotifModal = () => {
    setSelectedGuildIdsForEmailNotification(tempSelectedGuildIds)
    setSelectedTribeIdsForEmailNotification(tempSelectedTribeIds)
    setShowNotifModal(false)
  }

  const removeTribe = (tribe: Tribe) => {
    setSelectedTribes(selectedTribes.filter(t => t.id !== tribe.id))
    const newTeams = selectedTeams.filter(team => team.tribeId !== tribe.id)
    const newAllocations = { ...teamAllocations }
    Object.keys(newAllocations).forEach(key => {
      if (!newTeams.find(team => team.id === parseInt(key)))
        delete newAllocations[parseInt(key)]
    })
    setTeamAllocations(newAllocations)
    setSelectedTeams(newTeams)
  }

  const removeTeam = (team: Team) => {
    const newTeams = selectedTeams.filter(t => t.id !== team.id)
    const newAllocations = { ...teamAllocations }
    Object.keys(newAllocations).forEach(key => {
      if (!newTeams.find(t => t.id === parseInt(key)))
        delete newAllocations[parseInt(key)]
    })
    setTeamAllocations(newAllocations)
    setSelectedTeams(newTeams)
  }

  const addTeam = (team: Team) => {
    setSelectedTeams([...selectedTeams, team])
    setTeamAllocations({
      ...teamAllocations,
      [team.id]: { allocation: 0, role: '' },
    })
  }

  const toggleReviewerGuild = (guildId: number) => {
    setSelectedGuildIdsForGrowthWheelReviewer(prev =>
      prev.includes(guildId)
        ? prev.filter(id => id !== guildId)
        : [...prev, guildId],
    )
  }

  return (
    <>
      <section className="employee-form__section">
        <p className="employee-form__subtitle">Member Settings</p>
        <div className="employee-form__settings">
          <div>
            <FormCheckbox
              id="isAdminCheck"
              checked={isAdmin}
              label="This member is an Admin"
              onChange={checked => {
                setIsAdmin(checked)
                if (!checked) {
                  setSelectedGuildIdsForEmailNotification([])
                  setSelectedTribeIdsForEmailNotification([])
                }
              }}
            />
            {isAdmin && (
              <div className="employee-form__nested">
                <div className="employee-form__callout">
                  <p className="employee-form__callout-title">
                    Time Off Notification Email
                  </p>
                  <p className="employee-form__callout-text">
                    An email will automatically be generated each week to show
                    admin users who will be taking time off in the upcoming
                    week. I would like to receive information of:
                  </p>
                  <button
                    type="button"
                    className="employee-form__btn employee-form__btn--submit"
                    onClick={openNotifModal}
                  >
                    Notification Settings
                  </button>
                </div>
              </div>
            )}
          </div>

          <FormCheckbox
            id="isGuildMasterCheck"
            checked={isGuildMaster}
            label="This member is a Guild Master"
            onChange={setIsGuildMaster}
          />
          <FormCheckbox
            id="isLeadershipCheck"
            checked={isLeadership}
            label="This member is in Leadership"
            onChange={setIsLeadership}
          />

          <div>
            <FormCheckbox
              id="isGrowthWheelReviewerCheck"
              checked={isGrowthWheelReviewer}
              label="This member is a Growth Wheel Reviewer"
              onChange={setIsGrowthWheelReviewer}
            />
            {isGrowthWheelReviewer && (
              <div className="employee-form__nested">
                <div className="employee-form__callout employee-form__callout--reviewer">
                  <div className="employee-form__callout-title employee-form__callout-title--md">
                    Growth Wheel Reviewer
                  </div>
                  <p
                    className="employee-form__callout-text"
                    style={{ marginBottom: 0 }}
                  >
                    This member will be authorized to provide guided assessments
                    and feedback for all contributors within their selected
                    guilds.
                  </p>
                </div>
                <div className="employee-form__field">
                  <label className="employee-form__label">
                    Select Guilds to Review
                  </label>
                  <div className="employee-form__chips">
                    {selectedGuildIdsForGrowthWheelReviewer.map(guildId => {
                      const guild = guilds.find(g => g.id === guildId)
                      if (!guild) return null
                      return (
                        <button
                          key={`reviewer-guild-selected-${guild.id}`}
                          type="button"
                          className="employee-form__chip employee-form__chip--selected"
                          onClick={() => toggleReviewerGuild(guild.id)}
                        >
                          <span>{guild.role}</span>
                          <XIcon size={12} weight="regular" />
                        </button>
                      )
                    })}
                    {guilds
                      .filter(
                        guild =>
                          !selectedGuildIdsForGrowthWheelReviewer.includes(
                            guild.id,
                          ),
                      )
                      .map(guild => (
                        <button
                          key={`reviewer-guild-available-${guild.id}`}
                          type="button"
                          className="employee-form__chip employee-form__chip--available"
                          onClick={() => toggleReviewerGuild(guild.id)}
                        >
                          <span>{guild.role}</span>
                        </button>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <FormCheckbox
            id="isGrowthWheelEditorCheck"
            checked={isGrowthWheelEditor}
            label="This member is a Growth Wheel Editor"
            onChange={setIsGrowthWheelEditor}
          />

          <FormCheckbox
            id="isStaffCheck"
            checked={isStaff}
            label="This member is in Staff Augmentation Loan"
            onChange={setIsStaff}
          />
        </div>

        <div className="employee-form__tribe-team">
          <div className="employee-form__field">
            <label className="employee-form__label">
              Select a Tribe <span className="employee-form__required">*</span>
            </label>
            <div className="employee-form__chips">
              {selectedTribes.map(
                tribe =>
                  tribe.id && (
                    <button
                      key={`tribe-selected-${tribe.id}`}
                      type="button"
                      className="employee-form__chip employee-form__chip--selected"
                      onClick={e => {
                        e.preventDefault()
                        removeTribe(tribe)
                      }}
                    >
                      <span>{tribe.name}</span>
                      <XIcon size={12} weight="regular" />
                    </button>
                  ),
              )}
              {tribes.length === 0 ? (
                <p className="employee-form__hint">
                  {`There aren't tribes avalaible ${selectedTribes.length}`}
                </p>
              ) : (
                tribes.map(
                  tribe =>
                    tribe.id &&
                    !selectedTribes.includes(tribe) && (
                      <button
                        key={`tribe-available-${tribe.id}`}
                        type="button"
                        className="employee-form__chip employee-form__chip--available"
                        onClick={() =>
                          tribe.id &&
                          setSelectedTribes([...selectedTribes, tribe])
                        }
                      >
                        <span>{tribe.name}</span>
                      </button>
                    ),
                )
              )}
            </div>
          </div>

          {tribes.length > 0 && (
            <div className="employee-form__field">
              <label className="employee-form__label">
                Select a Team <span className="employee-form__required">*</span>
              </label>
              <div className="employee-form__chips">
                {selectedTeams.map(
                  team =>
                    team.id && (
                      <button
                        key={`team-selected-${team.id}`}
                        type="button"
                        className="employee-form__chip employee-form__chip--selected"
                        onClick={e => {
                          e.preventDefault()
                          removeTeam(team)
                        }}
                      >
                        <span>{team.name}</span>
                        <XIcon size={12} weight="regular" />
                      </button>
                    ),
                )}
                {selectableTeams.length === 0 && selectedTeams.length === 0 ? (
                  <p className="employee-form__hint">
                    Select a tribe to see its teams
                  </p>
                ) : (
                  availableTeams.map(
                    team =>
                      team.id &&
                      !selectedTeams.includes(team) && (
                        <button
                          key={`team-available-${team.id}`}
                          type="button"
                          className="employee-form__chip employee-form__chip--available"
                          onClick={() => addTeam(team)}
                        >
                          <span>{team.name}</span>
                        </button>
                      ),
                  )
                )}
              </div>
            </div>
          )}

          {Object.keys(teamAllocations).length > 0 && (
            <div className="employee-form__allocations">
              {Object.keys(teamAllocations).map(key => {
                const teamId = parseInt(key)
                const teamName =
                  teams.find(team => team.id === teamId)?.name || ''
                return (
                  <div
                    key={`team-allocation-${key}`}
                    className="employee-form__allocation-card"
                  >
                    <p className="employee-form__allocation-title">
                      {teamName}
                    </p>
                    <div className="employee-form__grid employee-form__grid--2">
                      <div className="employee-form__field">
                        <label className="employee-form__label">
                          Team Allocation (Capacity %)
                        </label>
                        <input
                          className={`employee-form__input ${
                            allocationError ? 'employee-form__input--error' : ''
                          }`}
                          type="number"
                          min={0}
                          max={100}
                          value={`${teamAllocations[teamId].allocation}`}
                          placeholder="e.g. 100"
                          onChange={e => {
                            if (allocationError) setAllocationError(false)
                            const changedAllocations = { ...teamAllocations }
                            const newAllocation = parseInt(
                              (e.target.value || '0').replace(/%/g, ''),
                            )
                            changedAllocations[teamId].allocation =
                              newAllocation > 100 ? 100 : newAllocation
                            setTeamAllocations(changedAllocations)
                          }}
                        />
                      </div>
                      {shouldRenderLabelsSelect() && (
                        <div className="employee-form__field">
                          <label className="employee-form__label">
                            Label as Team Lead or Support
                          </label>
                          <div className="employee-form__select-wrapper">
                            <select
                              className="employee-form__select-input"
                              value={teamAllocations[teamId].role}
                              id={`createEmployeeRoleSelect-${teamId}`}
                              onChange={e => {
                                setTeamAllocations({
                                  ...teamAllocations,
                                  [teamId]: {
                                    allocation:
                                      teamAllocations[teamId].allocation,
                                    role: e.target.value,
                                  },
                                })
                              }}
                            >
                              <option value="">No Label</option>
                              {TeamRoles[
                                guilds.find(guild => guild.id === selectedGuild)
                                  ?.type ?? ''
                              ]?.map((teamLabel, index) => (
                                <option
                                  key={`team-role-${teamId}-${index}`}
                                  value={teamLabel}
                                >
                                  {teamLabel}
                                </option>
                              ))}
                            </select>
                            <span className="employee-form__select-icon">
                              <CaretDownIcon size={14} weight="regular" />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {showNotifModal && (
        <NotificationSettingsModal
          tribes={tribes}
          guilds={guilds}
          tempSelectedTribeIds={tempSelectedTribeIds}
          tempSelectedGuildIds={tempSelectedGuildIds}
          onTempTribeIdsChange={setTempSelectedTribeIds}
          onTempGuildIdsChange={setTempSelectedGuildIds}
          onClose={closeNotifModal}
          onSave={saveNotifModal}
        />
      )}
    </>
  )
})
